import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './PrivateSector.css';

// Images
import Hipaa from '../Public/Images/Hipaa.png';
import PCI from '../Public/Images/PCI.webp';
import SOC2 from '../Public/Images/SOC2.png';
import ISO27001 from '../Public/Images/ISO-27001.png';
import GDPR from '../Public/Images/GDPR.png';
import CCPA from '../Public/Images/CCPA.webp';

function PrivateSector() {
    const [activePopup, setActivePopup] = useState(null);
    const [isDark, setIsDark] = useState(() => {
        try {
            return document.documentElement.getAttribute('data-theme') === 'dark' || localStorage.getItem('theme') === 'dark';
        } catch (e) {
            return false;
        }
    });
    const rootRef = useRef(null);
    const journeyRef = useRef(null);
    const complianceRef = useRef(null);
    const caseStudiesRef = useRef(null);

    useEffect(() => {
        // Observe changes to html[data-theme] so we can mirror a .dark class locally
        const observer = new MutationObserver(() => {
            const darkNow = document.documentElement.getAttribute('data-theme') === 'dark';
            setIsDark(darkNow);
            // Direct DOM manipulation to guarantee overrides for tough cascade conflicts
            try {
                if (rootRef.current) {
                    if (darkNow) rootRef.current.classList.add('dark');
                    else rootRef.current.classList.remove('dark');
                }
                if (journeyRef.current) {
                    if (darkNow) {
                        journeyRef.current.style.setProperty('background', '#17181a', 'important');
                        journeyRef.current.style.setProperty('background-image', 'none', 'important');
                        journeyRef.current.style.setProperty('color', '#e6eef8', 'important');
                        // ensure child steps
                        Array.from(journeyRef.current.querySelectorAll('.journey-step')).forEach((el) => {
                            el.style.setProperty('background', '#212226', 'important');
                            el.style.setProperty('color', '#e5e5e5', 'important');
                            el.style.setProperty('padding', '1rem', 'important');
                            el.style.setProperty('border-radius', '8px', 'important');
                        });
                        Array.from(journeyRef.current.querySelectorAll('.step-number')).forEach((el) => {
                            el.style.setProperty('background-color', '#2563eb', 'important');
                            el.style.setProperty('color', '#ffffff', 'important');
                            el.style.setProperty('border', '2px solid rgba(255,255,255,0.06)', 'important');
                        });
                    } else {
                        // remove inline styles so CSS rules take over for light theme
                        journeyRef.current.style.removeProperty('background');
                        journeyRef.current.style.removeProperty('background-image');
                        journeyRef.current.style.removeProperty('color');
                        Array.from(journeyRef.current.querySelectorAll('.journey-step')).forEach((el) => {
                            el.style.removeProperty('background');
                            el.style.removeProperty('color');
                            el.style.removeProperty('padding');
                            el.style.removeProperty('border-radius');
                        });
                        Array.from(journeyRef.current.querySelectorAll('.step-number')).forEach((el) => {
                            el.style.removeProperty('background-color');
                            el.style.removeProperty('color');
                            el.style.removeProperty('border');
                        });
                    }
                }
                // Force compliance-section and inner columns
                if (complianceRef.current) {
                    if (darkNow) {
                        complianceRef.current.style.setProperty('background', '#15171a', 'important');
                        complianceRef.current.style.setProperty('background-image', 'none', 'important');
                        complianceRef.current.style.setProperty('color', '#e6eef8', 'important');
                        Array.from(complianceRef.current.querySelectorAll('.compliance-column')).forEach((el) => {
                            el.style.setProperty('background', '#1f1f1f', 'important');
                            el.style.setProperty('background-image', 'none', 'important');
                            el.style.setProperty('color', '#e5e5e5', 'important');
                            el.style.setProperty('box-shadow', '0 4px 12px rgba(0,0,0,0.6)', 'important');
                        });
                    } else {
                        complianceRef.current.style.removeProperty('background');
                        complianceRef.current.style.removeProperty('background-image');
                        complianceRef.current.style.removeProperty('color');
                        Array.from(complianceRef.current.querySelectorAll('.compliance-column')).forEach((el) => {
                            el.style.removeProperty('background');
                            el.style.removeProperty('background-image');
                            el.style.removeProperty('color');
                            el.style.removeProperty('box-shadow');
                        });
                    }
                }
                // Force case-studies-section and its cards
                if (caseStudiesRef.current) {
                    if (darkNow) {
                        caseStudiesRef.current.style.setProperty('background', '#15171a', 'important');
                        caseStudiesRef.current.style.setProperty('background-image', 'none', 'important');
                        caseStudiesRef.current.style.setProperty('color', '#e6eef8', 'important');
                        Array.from(caseStudiesRef.current.querySelectorAll('.case-study')).forEach((el) => {
                            el.style.setProperty('background', '#1f1f1f', 'important');
                            el.style.setProperty('color', '#e5e5e5', 'important');
                            el.style.setProperty('box-shadow', '0 4px 12px rgba(0,0,0,0.6)', 'important');
                        });
                    } else {
                        caseStudiesRef.current.style.removeProperty('background');
                        caseStudiesRef.current.style.removeProperty('background-image');
                        caseStudiesRef.current.style.removeProperty('color');
                        Array.from(caseStudiesRef.current.querySelectorAll('.case-study')).forEach((el) => {
                            el.style.removeProperty('background');
                            el.style.removeProperty('color');
                            el.style.removeProperty('box-shadow');
                        });
                    }
                }
            } catch (e) {
                // ignore DOM errors in non-browser environments
            }
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        // Run once initially to set classes/styles based on current theme
        const initial = document.documentElement.getAttribute('data-theme') === 'dark';
        if (initial) {
            try {
                if (rootRef.current) rootRef.current.classList.add('dark');
                if (journeyRef.current) {
                    journeyRef.current.style.setProperty('background', '#17181a', 'important');
                    journeyRef.current.style.setProperty('background-image', 'none', 'important');
                    journeyRef.current.style.setProperty('color', '#e6eef8', 'important');
                }
                if (complianceRef.current) {
                    complianceRef.current.style.setProperty('background', '#15171a', 'important');
                    complianceRef.current.style.setProperty('background-image', 'none', 'important');
                    complianceRef.current.style.setProperty('color', '#e6eef8', 'important');
                    Array.from(complianceRef.current.querySelectorAll('.compliance-column')).forEach((el) => {
                        el.style.setProperty('background', '#1f1f1f', 'important');
                        el.style.setProperty('background-image', 'none', 'important');
                        el.style.setProperty('color', '#e5e5e5', 'important');
                    });
                }
                if (caseStudiesRef.current) {
                    caseStudiesRef.current.style.setProperty('background', '#15171a', 'important');
                    caseStudiesRef.current.style.setProperty('background-image', 'none', 'important');
                    caseStudiesRef.current.style.setProperty('color', '#e6eef8', 'important');
                    Array.from(caseStudiesRef.current.querySelectorAll('.case-study')).forEach((el) => {
                        el.style.setProperty('background', '#1f1f1f', 'important');
                        el.style.setProperty('color', '#e5e5e5', 'important');
                    });
                }
            } catch (e) {}
        }
        return () => observer.disconnect();
    }, []);

    const openPopup = (index) => {
        setActivePopup(index);
    };

    const closePopup = () => {
        setActivePopup(null);
    };

    const features = [
        {
            title: 'Containerized Services',
            summary: 'Consistent, lightweight OpenStack services for faster deployments.',
            details: (
                <>
                    <h4>Streamlined Deployment with Kolla</h4>
                    <p>Kolla delivers curated, production-ready container images for OpenStack services (Keystone, Nova, Neutron, Cinder, Glance, Horizon, etc.), built with Jinja2-templated Dockerfiles for customization. Kolla-Ansible provides playbooks to deploy and manage these containers across hosts, ensuring simplicity for regulated industries like healthcare and finance. Optional Kayobe automates bare-metal setup, enabling rapid, compliant cloud deployment.</p>
                    <h4>Why It Matters</h4>
                    <ul>
                        <li>Containers ensure consistent, isolated services, reducing risks for HIPAA and GDPR compliance.</li>
                        <li>Ansible-driven lifecycle (deploy, upgrade, teardown) supports auditable, repeatable operations.</li>
                        <li>TLS encryption secures API and inter-service traffic for sensitive workloads.</li>
                        <li>Seamless integration with enterprise storage like Ceph for scalable data management.</li>
                    </ul>
                    <h4>Eupraxia Labs Enhancements</h4>
                    <ul>
                        <li><strong>Hardened Stacks</strong>: Pre-vetted manifests for OpenStack releases, optimized for private sector compliance.</li>
                        <li><strong>GitOps Integration</strong>: Configurations managed via Git for traceability, ideal for audit-heavy industries.</li>
                        <li><strong>Automated Security</strong>: TLS and SSO integration for secure, compliant deployments.</li>
                        <li><strong>Self-Service Operations</strong>: Simplified scaling and service additions for fast-paced environments.</li>
                    </ul>
                    <p>Eupraxia Labs’ Fleet Management Cluster (FMC) transforms Kolla into a turnkey solution, delivering secure, scalable clouds in hours for regulated industries.</p>
                </>
            )
        },
        {
            title: 'GitOps-Native Management',
            summary: 'Manage upgrades and configurations through Git, eliminating brittle scripts.',
            details: (
                <>
                    <h4>GitOps for Regulated Industries</h4>
                    <p>GitOps uses Git as the single source of truth for infrastructure and application management, powered by ArgoCD. All configurations and manifests are versioned, reviewed, and automatically applied, ensuring compliance with standards like PCI-DSS and SOC 2. This approach provides traceability and automation for private sector workloads.</p>
                    <h4>How It Works</h4>
                    <ul>
                        <li><strong>Version Control</strong>: Git repositories store manifests, triggering automated pipelines for changes.</li>
                        <li><strong>Continuous Reconciliation</strong>: ArgoCD ensures clusters match the declared Git state, preventing drift.</li>
                        <li><strong>Secure Access</strong>: Role-based controls via Keycloak ensure authorized deployments.</li>
                        <li><strong>Observability</strong>: Grafana, Loki, and Tempo provide real-time audit trails.</li>
                    </ul>
                    <h4>Benefits</h4>
                    <ul>
                        <li><strong>Reduced Overhead</strong>: Eliminates manual scripting for consistent operations.</li>
                        <li><strong>Compliance Ready</strong>: Git commits log all changes for audit trails.</li>
                        <li><strong>Scalability</strong>: Manages hybrid and multi-cloud clusters effortlessly.</li>
                        <li><strong>Developer Focus</strong>: Automates deployments, freeing teams for innovation.</li>
                    </ul>
                    <h4>Eupraxia Labs Advantage</h4>
                    <ul>
                        <li><strong>Pre-built Pipelines</strong>: Secure, compliant cluster bootstrapping.</li>
                        <li><strong>Multi-tenant Support</strong>: Tailored for enterprise governance needs.</li>
                        <li><strong>AI Validation</strong>: PioneerAI™ detects misconfigurations before deployment.</li>
                    </ul>
                    <p>ArgoCD in our platform ensures auditable, automated management, ideal for regulated industries requiring robust governance.</p>
                </>
            )
        },
        {
            title: 'Ceph-Backed Resilience',
            summary: 'Enterprise-grade block and object storage with high availability.',
            details: (
                <>
                    <h4>Robust Storage with Ceph</h4>
                    <p>Ceph is a scalable, open-source storage system supporting object, block, and file storage (CephFS, RBD, RGW), ideal for sensitive data in healthcare and finance. Its self-healing and high-availability features ensure data integrity and uptime for regulated workloads.</p>
                    <h4>Challenges Addressed</h4>
                    <ul>
                        <li>Simplifies daemon management (monitors, OSDs, metadata servers).</li>
                        <li>Automates CRUSH map configuration for optimal data placement.</li>
                        <li>Optimizes performance for enterprise workloads.</li>
                        <li>Enhances monitoring to prevent data loss during scaling.</li>
                    </ul>
                    <h4>Eupraxia Labs Simplifications</h4>
                    <ul>
                        <li><strong>Outpost GUI</strong>: Intuitive interface with resizable columns and refresh controls.</li>
                        <li><strong>Guided Workflows</strong>: Streamlined CLI and configuration management.</li>
                        <li><strong>Automated Monitoring</strong>: Real-time visibility into cluster health.</li>
                        <li><strong>Scaling Tools</strong>: Error-free capacity additions with minimal disruption.</li>
                    </ul>
                    <h4>Business Benefits</h4>
                    <ul>
                        <li><strong>Rapid Deployment</strong>: Faster setup for compliance-driven storage.</li>
                        <li><strong>Reduced Risk</strong>: Minimized misconfiguration and data loss.</li>
                        <li><strong>Accessibility</strong>: Non-experts can manage Ceph effectively.</li>
                        <li><strong>Scalability</strong>: Seamless expansion for growing data needs.</li>
                    </ul>
                    <p>Eupraxia Labs makes Ceph a user-friendly, resilient storage solution for regulated industries, ensuring compliance and reliability.</p>
                </>
            )
        },
        {
            title: 'AI-Optimized Infrastructure',
            summary: 'GPU-ready Kubernetes for AI, ML, and secure VDI workloads.',
            details: (
                <>
                    <h4>Powering AI and ML Workloads</h4>
                    <p>VitroAI™’s AI-Optimized Infrastructure leverages GPU-ready Kubernetes to support high-performance AI, machine learning, and virtual desktop infrastructure (VDI) workloads. Tailored for private sector applications like fraud detection and patient analytics, it ensures secure, scalable processing for data-intensive tasks.</p>
                    <h4>Key Features</h4>
                    <ul>
                        <li><strong>GPU Support</strong>: Optimized for NVIDIA and AMD GPUs, enabling fast AI/ML model training.</li>
                        <li><strong>Secure VDI</strong>: Isolated, encrypted virtual desktops for sensitive applications.</li>
                        <li><strong>Kubernetes Integration</strong>: Seamless scaling and orchestration for AI workloads.</li>
                        <li><strong>Compliance Ready</strong>: Aligns with HIPAA, GDPR, and CCPA for regulated data.</li>
                    </ul>
                    <h4>Eupraxia Labs Enhancements</h4>
                    <ul>
                        <li><strong>Pre-configured Pipelines</strong>: Automated setup for AI/ML clusters with ArgoCD.</li>
                        <li><strong>Observability</strong>: Grafana dashboards for monitoring GPU and workload performance.</li>
                        <li><strong>Security Integration</strong>: Zero Trust principles for AI data protection.</li>
                        <li><strong>Scalability</strong>: Dynamic resource allocation for growing AI demands.</li>
                    </ul>
                    <h4>Business Benefits</h4>
                    <ul>
                        <li><strong>Faster Innovation</strong>: Accelerates AI model development and deployment.</li>
                        <li><strong>Secure Processing</strong>: Protects sensitive data in regulated industries.</li>
                        <li><strong>Cost Efficiency</strong>: Optimizes GPU resource usage for cost savings.</li>
                        <li><strong>Flexibility</strong>: Supports hybrid and edge deployments for diverse use cases.</li>
                    </ul>
                    <p>VitroAI™’s AI-Optimized Infrastructure empowers private sector organizations to harness AI and ML securely and efficiently.</p>
                </>
            )
        },
        {
            title: 'PCI-DSS Compliance',
            summary: 'Tokenization, encrypted transactions, audit logging.',
            details: (
                <>
                    <h4>Secure Financial Transactions</h4>
                    <p>VitroAI™ supports PCI-DSS compliance with robust security controls for financial transactions. Designed for industries like banking and e-commerce, it ensures data protection and auditability to meet stringent regulatory requirements.</p>
                    <h4>Key Features</h4>
                    <ul>
                        <li><strong>Tokenization</strong>: Replaces sensitive cardholder data with secure tokens.</li>
                        <li><strong>Encrypted Transactions</strong>: End-to-end encryption for payment processing.</li>
                        <li><strong>Audit Logging</strong>: Comprehensive logs for compliance audits.</li>
                        <li><strong>Network Security</strong>: Microsegmentation to isolate payment workloads.</li>
                    </ul>
                    <h4>Eupraxia Labs Enhancements</h4>
                    <ul>
                        <li><strong>Automated Compliance</strong>: GitOps pipelines embed PCI-DSS controls.</li>
                        <li><strong>Zero Trust Integration</strong>: CAC/PIV and role-based access for secure operations.</li>
                        <li><strong>Monitoring</strong>: Real-time alerts for suspicious transaction activity.</li>
                        <li><strong>Scalability</strong>: Supports high-volume transaction environments.</li>
                    </ul>
                    <h4>Business Benefits</h4>
                    <ul>
                        <li><strong>Regulatory Compliance</strong>: Simplifies PCI-DSS audit preparation.</li>
                        <li><strong>Customer Trust</strong>: Secure transactions enhance brand reputation.</li>
                        <li><strong>Operational Efficiency</strong>: Automated controls reduce manual oversight.</li>
                        <li><strong>Risk Mitigation</strong>: Minimizes data breach risks in financial operations.</li>
                    </ul>
                    <p>VitroAI™’s PCI-DSS compliance features ensure secure, auditable financial operations for regulated industries. <em>(Compliance in progress)</em></p>
                </>
            )
        },
        {
            title: 'SOC 2 & ISO 27001',
            summary: 'Controls designed to pass enterprise audits.',
            details: (
                <>
                    <h4>Audit-Ready Security Controls</h4>
                    <p>VitroAI™ supports SOC 2 and ISO 27001 compliance with enterprise-grade controls for data security and operational integrity. Tailored for SaaS providers and enterprises, it ensures readiness for rigorous audits in regulated industries.</p>
                    <h4>Key Features</h4>
                    <ul>
                        <li><strong>Access Controls</strong>: Role-based permissions and SSO integration.</li>
                        <li><strong>Data Protection</strong>: FIPS-aligned encryption for sensitive data.</li>
                        <li><strong>Audit Trails</strong>: Comprehensive logging for SOC 2 and ISO 27001 audits.</li>
                        <li><strong>Monitoring</strong>: Real-time dashboards for compliance oversight.</li>
                    </ul>
                    <h4>Eupraxia Labs Enhancements</h4>
                    <ul>
                        <li><strong>Automated Compliance</strong>: GitOps ensures consistent control enforcement.</li>
                        <li><strong>Zero Trust Security</strong>: Continuous verification for audit compliance.</li>
                        <li><strong>Observability</strong>: Grafana and Loki for detailed compliance monitoring.</li>
                        <li><strong>Scalability</strong>: Supports enterprise-wide audit requirements.</li>
                    </ul>
                    <h4>Business Benefits</h4>
                    <ul>
                        <li><strong>Audit Readiness</strong>: Simplifies SOC 2 and ISO 27001 certification.</li>
                        <li><strong>Data Security</strong>: Protects customer data in regulated environments.</li>
                        <li><strong>Operational Trust</strong>: Enhances credibility with enterprise clients.</li>
                        <li><strong>Efficiency</strong>: Reduces audit preparation time and costs.</li>
                    </ul>
                    <p>VitroAI™’s SOC 2 and ISO 27001 controls streamline compliance for enterprises, ensuring secure and auditable operations. <em>(Compliance in progress)</em></p>
                </>
            )
        }
    ];

    // Inline style for hero and CTA to guarantee dark background when theme toggles
    const sectionStyle = isDark ? { background: '#16171a', backgroundImage: 'none', color: '#e6eef8' } : {};

    return (
        <div ref={rootRef} className={`private-sector-page ${isDark ? 'dark' : ''}`}>
            <section className="hero-section-private" style={sectionStyle}>
                <div className="hero-content">
                    <h1>Infrastructure That Speeds Compliance, Not Just Promises It</h1>
                    <p>HIPAA, PCI-DSS, SOC 2, and ISO 27001 are not features you can “turn on.” They are audited outcomes. VitroAI™ equips your teams with the built-in security, logging, encryption, and documentation to make those audits faster, simpler, and less costly.</p>
                    <div className="cta-buttons">
                        <Link to="/contact" className="cta-button-private">Explore Private Sector Solutions</Link>
                    </div>
                </div>
            </section>
            <section className="why-section">
                <h2>Why VitroAI™ for Business</h2>
                <div className="columns-container">
                    <div className="column">
                        <span className="icon" aria-label="Containerized services icon">📦</span>
                        <h3>Containerized Services</h3>
                        <p>Lightweight, consistent deployments with minimal operational drift.</p>
                    </div>
                    <div className="column">
                        <span className="icon" aria-label="GitOps management icon">📜</span>
                        <h3>GitOps-Native Management</h3>
                        <p>Declarative, version-controlled changes via Git for seamless operations.</p>
                    </div>
                    <div className="column">
                        <span className="icon" aria-label="Enterprise hardening icon">🔒</span>
                        <h3>Enterprise Hardening</h3>
                        <p>Secure UI, automated monitoring, and compliance-ready controls.</p>
                    </div>
                </div>
            </section>
            <section className="features-section">
                <h2>Features</h2>
                <div className="features-container-privateSector">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="feature-card"
                            onClick={() => openPopup(index)}
                        >
                            <h3>{feature.title} {feature.title.includes('Compliance') ? <span className="coming-soon"></span> : null}</h3>
                            <p>{feature.summary}</p>
                        </div>
                    ))}
                </div>
            </section>
            {activePopup !== null && (
                <div className="popup-overlay" onClick={closePopup}>
                    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                        <button className="popup-close" onClick={closePopup}>×</button>
                        <h3>{features[activePopup].title}</h3>
                        <div className="popup-details">
                            {features[activePopup].details}
                        </div>
                    </div>
                </div>
            )}
            <section className="case-studies-section" ref={caseStudiesRef}>
                <h2>Industry-Specific Case Studies</h2>
                <div className="carousel">
                    <div className="case-study">
                        <h3>Healthcare</h3>
                        <p>“Accelerating patient research securely”</p>
                    </div>
                    <div className="case-study">
                        <h3>Finance</h3>
                        <p>“Protecting customer transactions at scale”</p>
                    </div>
                    <div className="case-study">
                        <h3>Enterprise SaaS</h3>
                        <p>“Meeting SOC 2 compliance faster”</p>
                    </div>
                </div>
            </section>
            <section className="compliance-section" ref={complianceRef}>
                <h2>Compliance Without the Guesswork</h2>
                <div className="compliance-container">
                    <div className="compliance-column">
                        <h3>What We Provide</h3>
                        <ul>
                            <li>Encryption at rest and in transit, tokenization, and audit logs</li>
                            <li>Secure VDI workspaces for HIPAA/HITECH workloads</li>
                            <li>Role-based access control & centralized identity management</li>
                            <li>Pre-integrated observability and reporting tools</li>
                        </ul>
                    </div>
                    <div className="compliance-column">
                        <h3>What You Achieve Faster</h3>
                        <ul>
                            <li>HIPAA attestation for healthcare workloads</li>
                            <li>PCI-DSS certification for payment systems</li>
                            <li>SOC 2 Type II reports for SaaS offerings</li>
                            <li>ISO 27001 alignment with reduced overhead</li>
                        </ul>
                    </div>
                </div>
                <div className="badge-bar">
                    <div className="badge-item">
                        <img src={Hipaa} alt="HIPAA compliant" className="badge" />
                        <span className="badge-label">HIPAA</span>
                    </div>
                    <div className="badge-item">
                        <img src={PCI} alt="PCI-DSS compliant" className="badge" style={{ opacity: 0.5 }} />
                        <span className="badge-label">PCI-DSS</span>
                    </div>
                    <div className="badge-item">
                        <img src={SOC2} alt="SOC 2 compliant" className="badge" style={{ opacity: 0.5 }} />
                        <span className="badge-label">SOC 2</span>
                    </div>
                    <div className="badge-item">
                        <img src={ISO27001} alt="ISO 27001 compliant" className="badge" style={{ opacity: 0.5 }} />
                        <span className="badge-label">ISO 27001</span>
                    </div>
                    <div className="badge-item">
                        <img src={GDPR} alt="GDPR compliant" className="badge" />
                        <span className="badge-label">GDPR</span>
                    </div>
                    <div className="badge-item">
                        <img src={CCPA} alt="CCPA compliant" className="badge" />
                        <span className="badge-label">CCPA</span>
                    </div>
                </div>
                {/* Compliance journey: apply inline styles when dark to guarantee overrides */}
                <div
                    className="compliance-journey"
                    ref={journeyRef}
                    style={isDark ? { background: '#17181a', backgroundImage: 'none', color: '#e6eef8' } : {}}
                >
                    <h3 style={isDark ? { color: '#ffffff' } : {}}>Your Compliance Journey</h3>
                    <div className="journey-steps" style={isDark ? { gap: '1rem' } : {}}>
                        <div className="journey-step" style={isDark ? { background: '#212226', color: '#e5e5e5', padding: '1rem', borderRadius: '8px' } : {}}>
                            <span className="step-number" style={isDark ? { backgroundColor: '#2563eb', color: '#ffffff', border: '2px solid rgba(255,255,255,0.06)' } : {}}>1</span>
                            <p style={isDark ? { color: '#dfeffc' } : {}}>VitroAI™ Built-In Controls</p>
                        </div>
                        <span className="journey-arrow" style={isDark ? { color: '#cfe6ff', fontWeight: 700 } : {}}>→</span>
                        <div className="journey-step" style={isDark ? { background: '#212226', color: '#e5e5e5', padding: '1rem', borderRadius: '8px' } : {}}>
                            <span className="step-number" style={isDark ? { backgroundColor: '#2563eb', color: '#ffffff', border: '2px solid rgba(255,255,255,0.06)' } : {}}>2</span>
                            <p style={isDark ? { color: '#dfeffc' } : {}}>Audit Readiness Tools<br/>(Armo/Kubescape)</p>
                        </div>
                        <span className="journey-arrow" style={isDark ? { color: '#cfe6ff', fontWeight: 700 } : {}}>→</span>
                        <div className="journey-step" style={isDark ? { background: '#212226', color: '#e5e5e5', padding: '1rem', borderRadius: '8px' } : {}}>
                            <span className="step-number" style={isDark ? { backgroundColor: '#2563eb', color: '#ffffff', border: '2px solid rgba(255,255,255,0.06)' } : {}}>3</span>
                            <p style={isDark ? { color: '#dfeffc' } : {}}>Faster, Cheaper Certification</p>
                        </div>
                    </div>
                </div>
                <p className="compliance-positioning">VitroAI™ doesn’t replace your auditor — it gets you ready for them.</p>
            </section>
            <section className="cta-section-private" style={sectionStyle}>
                <h2>Build with confidence in any regulated industry.</h2>
                <Link to="/contact" className="cta-button-private">Contact Our Enterprise Team</Link>
            </section>
        </div>
    );
}

export default PrivateSector;
